import { useQuery } from '@tanstack/react-query';
import { StrapiHeaderResponse, HeaderDiscount } from '@/types/header';

// Mapa de nombres de categorías del menú a IDs de PrestaShop para Strapi
const CATEGORY_NAME_TO_PRESTASHOP_ID: { [key: string]: number } = {
  // Categorías principales
  'LA DESPENSA ITALIANA': 11,
  'Productos lácteos y yogures': 23,
  'ALIMENTOS FRESCOS': 0,
  'BEBIDAS': 0,
  'CUIDADO PERSONAL': 0,
  'LIMPIEZA Y CUIDADO DEL HOGAR': 0,
  
  // Subcategorías
  'Desayuno': 12,
};

interface UseStrapiHeaderReturn {
  headerData: StrapiHeaderResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  
  // Funciones helper
  getDiscountsByCategoryId: (categoryId: number) => HeaderDiscount[] | null;
  getCategoryDiscounts: (categoryId: number, categoryName?: string) => HeaderDiscount[];
  getAllDiscounts: () => HeaderDiscount[];
  extractTextFromStrapiText: (textElements: any[]) => string;
  getComplementaryText: () => { texto: string; url: string } | null;
  getTopDiscount: () => string | null;
  getLogo: () => { url: string; alt: string } | null;
}

const fetchHeaderData = async (): Promise<StrapiHeaderResponse> => {
  const response = await fetch('/api/strapi/header');
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
    throw new Error(`Error HTTP ${response.status}: ${errorData.error || response.statusText}`);
  }
  
  const data: StrapiHeaderResponse = await response.json();
  
  // Validar que los datos tengan una estructura esperada
  if (!data || !data.data) {
    throw new Error('Estructura de datos inválida recibida del servidor');
  }
  
  return data;
};

export const useStrapiHeader = (): UseStrapiHeaderReturn => {
  const {
    data: headerData,
    isLoading: loading,
    error,
    refetch
  } = useQuery<StrapiHeaderResponse, Error>({
    queryKey: ['strapi-header'],
    queryFn: fetchHeaderData,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos (gcTime reemplaza a cacheTime en v5)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Función para obtener descuentos por ID de categoría de Prestashop
  const getDiscountsByCategoryId = (categoryId: number): HeaderDiscount[] | null => {
    if (!headerData?.data) return null;
    
    // Verificar la nueva estructura (Productos)
    if (headerData.data.Productos && Array.isArray(headerData.data.Productos)) {
      const product = headerData.data.Productos.find(
        (item: any) => item.Menu_Desplegable.ID_Categoria_Prestashop === categoryId
      );
      
      return product?.Menu_Desplegable.Descuentos || null;
    }
    
    // Verificar la estructura anterior (Menu_Desplegable) para compatibilidad
    if (headerData.data.Menu_Desplegable && Array.isArray(headerData.data.Menu_Desplegable)) {
      const menuItem = headerData.data.Menu_Desplegable.find(
        (item: any) => item.Menu_Desplegable.ID_Categoria_Prestashop === categoryId
      );
      
      return menuItem?.Menu_Desplegable.Descuentos || null;
    }
    
    // Verificar si existe la estructura de descuentos antigua (para compatibilidad)
    if (headerData.data.Descuento && Array.isArray(headerData.data.Descuento)) {
      const category = headerData.data.Descuento.find(
        (cat: any) => cat.ID_Categoria_Prestashop === categoryId
      );
      
      return category?.Descuentos || null;
    }
    
    return null;
  };

  // Función mejorada para obtener descuentos de categorías (usada por useCategoryDiscounts)
  const getCategoryDiscounts = (categoryId: number, categoryName?: string): HeaderDiscount[] => {
    if (!headerData?.data) return [];
    
    // Buscar descuentos para la categoría específica en la nueva estructura (Productos)
    let categoryDiscount: any = null;
    
    if (headerData.data.Productos && Array.isArray(headerData.data.Productos)) {
      console.log(`🔍 Buscando descuentos para: "${categoryName}" (ID: ${categoryId})`);
      
      categoryDiscount = headerData.data.Productos.find(
        (product: any) => product.Menu_Desplegable.ID_Categoria_Prestashop === categoryId
      );
    }
    
    // Si no se encuentra en Productos, buscar en la estructura anterior (Menu_Desplegable)
    if (!categoryDiscount && headerData.data.Menu_Desplegable && Array.isArray(headerData.data.Menu_Desplegable)) {
      categoryDiscount = headerData.data.Menu_Desplegable.find(
        (item: any) => item.Menu_Desplegable.ID_Categoria_Prestashop === categoryId
      );
    }
    
    // Si no se encuentra en ninguna estructura nueva, buscar en la estructura antigua (Descuento)
    if (!categoryDiscount && headerData.data.Descuento && Array.isArray(headerData.data.Descuento)) {
      categoryDiscount = headerData.data.Descuento.find(
        (discount: any) => discount.ID_Categoria_Prestashop === categoryId
      );
    }
    
    // Intentar mapeo directo por nombre de categoría si no se encontró por ID
    let targetPrestaShopId = categoryId;
    if (!categoryDiscount && categoryName && CATEGORY_NAME_TO_PRESTASHOP_ID[categoryName]) {
      targetPrestaShopId = CATEGORY_NAME_TO_PRESTASHOP_ID[categoryName];
      console.log(`🔄 Intentando mapeo por nombre: "${categoryName}" -> ID: ${targetPrestaShopId}`);
      
      // Buscar nuevamente con el ID mapeado
      if (headerData.data.Productos && Array.isArray(headerData.data.Productos)) {
        categoryDiscount = headerData.data.Productos.find(
          (product: any) => product.Menu_Desplegable.ID_Categoria_Prestashop === targetPrestaShopId
        );
      }
      
      if (!categoryDiscount && headerData.data.Menu_Desplegable && Array.isArray(headerData.data.Menu_Desplegable)) {
        categoryDiscount = headerData.data.Menu_Desplegable.find(
          (item: any) => item.Menu_Desplegable.ID_Categoria_Prestashop === targetPrestaShopId
        );
      }
      
      if (!categoryDiscount && headerData.data.Descuento && Array.isArray(headerData.data.Descuento)) {
        categoryDiscount = headerData.data.Descuento.find(
          (discount: any) => discount.ID_Categoria_Prestashop === targetPrestaShopId
        );
      }
    }
    
    if (categoryDiscount) {
      console.log(`✅ Descuentos encontrados para "${categoryName}" (ID: ${targetPrestaShopId}):`, categoryDiscount);
      
      // Extraer descuentos según la estructura - con verificación de tipos
      if ('Menu_Desplegable' in categoryDiscount && categoryDiscount.Menu_Desplegable?.Descuentos) {
        return categoryDiscount.Menu_Desplegable.Descuentos;
      } else if ('Descuentos' in categoryDiscount && categoryDiscount.Descuentos) {
        return categoryDiscount.Descuentos;
      }
    } else {
      console.log(`❌ No se encontraron descuentos para "${categoryName}" (ID: ${categoryId})`);
    }
    
    return [];
  };

  // Función para extraer todos los descuentos (usada por useHeaderDiscounts)
  const getAllDiscounts = (): HeaderDiscount[] => {
    if (!headerData?.data) return [];
    
    const allDiscounts: HeaderDiscount[] = [];
    
    // Verificar la nueva estructura (Menu_Desplegable)
    if (headerData.data.Menu_Desplegable && Array.isArray(headerData.data.Menu_Desplegable)) {
      headerData.data.Menu_Desplegable.forEach((menuItem: any) => {
        if (menuItem.Menu_Desplegable?.Descuentos && Array.isArray(menuItem.Menu_Desplegable.Descuentos)) {
          allDiscounts.push(...menuItem.Menu_Desplegable.Descuentos);
        }
      });
    }
    
    // Verificar la estructura antigua (para compatibilidad)
    if (headerData.data.Descuento && Array.isArray(headerData.data.Descuento)) {
      headerData.data.Descuento.forEach((categoryDiscount: any) => {
        if (categoryDiscount.Descuentos && Array.isArray(categoryDiscount.Descuentos)) {
          allDiscounts.push(...categoryDiscount.Descuentos);
        }
      });
    }
    
    return allDiscounts;
  };

  // Función para extraer texto de los elementos de texto de Strapi
  const extractTextFromStrapiText = (textElements: any[]): string => {
    if (!Array.isArray(textElements)) return '';
    
    return textElements
      .map(element => 
        element.children
          ?.map((child: any) => child.text)
          .join('') || ''
      )
      .join(' ');
  };

  // Función para obtener el texto complementario
  const getComplementaryText = (): { texto: string; url: string } | null => {
    if (!headerData?.data?.Texto_Complementario) return null;
    
    const textData = headerData.data.Texto_Complementario;
    if (!textData.Visible) return null;
    
    return {
      texto: textData.Texto,
      url: textData.Url
    };
  };

  // Función para obtener el descuento superior (banner)
  const getTopDiscount = (): string | null => {
    return headerData?.data?.Descuento_Superior || null;
  };

  // Función para obtener el logo del header
  const getLogo = (): { url: string; alt: string } | null => {
    if (!headerData?.data?.Logo) return null;
    
    const logo = headerData.data.Logo;
    return {
      url: logo.url,
      alt: logo.alternativeText || 'Hiperalia Logo'
    };
  };

  return {
    headerData: headerData || null,
    loading,
    error: error?.message || null,
    refetch: () => refetch(),
    getDiscountsByCategoryId,
    getCategoryDiscounts,
    getAllDiscounts,
    extractTextFromStrapiText,
    getComplementaryText,
    getTopDiscount,
    getLogo
  };
};